/**
 * Grand livre des commissions (plateforme) — branché sur /api/admin/commissions.
 * Chaque ligne montre son taux et son palier : jamais un montant sans
 * explication. Vide tant qu'aucun paiement Stripe n'est passé — l'état vide
 * dit quoi faire.
 */
import { useEffect, useState } from 'react';
import { appelerApi } from '../../reseau.ts';
import { Badge, Card, GradientAmount, PageTitle, Table, eur } from '../ui.tsx';

type Ligne = {
  cree_le: string;
  palier: string;
  taux_applique: string;
  montant: string;
  statut: string;
  stripe_transfer_id: string | null;
  type: string;
  type_transaction: string;
  montant_transaction: string;
  payeur_nom: string | null;
  beneficiaire_nom: string;
};

export function Commissions() {
  const [lignes, setLignes] = useState<Ligne[] | null>(null);

  useEffect(() => {
    void appelerApi<{ commissions: Ligne[] }>('/api/admin/commissions').then((r) => {
      if (r.donnees) setLignes(r.donnees.commissions);
    });
  }, []);

  const total = (lignes ?? []).reduce((somme, l) => somme + Number(l.montant), 0);

  return (
    <>
      <PageTitle
        title="Commissions — grand livre"
        subtitle="Toutes les commissions versées ou dues, avec leur taux, leur palier et leur virement Stripe. Journal en ajout seul : rien ne s'y réécrit."
      />

      {lignes === null ? (
        <p className="text-fumee">Chargement…</p>
      ) : lignes.length === 0 ? (
        <Card>
          <p className="text-[15px] leading-relaxed text-fumee">
            Aucune commission pour l'instant — elles apparaîtront ici dès le premier paiement
            Stripe (abonnement Premium ou sponsoring) reçu sur la plateforme. Vérifiez que les clés
            Stripe sont configurées dans Intégrations, et que les relations de commission
            (organisateur → sponsor, apporteur → organisateur) sont attribuées.
          </p>
        </Card>
      ) : (
        <>
          <Table head={['Date', 'Transaction', 'Payeur', 'Bénéficiaire', 'Type', 'Taux', 'Montant', 'Statut']}>
            {lignes.map((l, i) => (
              <tr key={i}>
                <td className="text-fumee">
                  {new Date(l.cree_le).toLocaleDateString('fr-BE', { day: 'numeric', month: 'short' })}
                </td>
                <td className="text-fumee">
                  {l.type_transaction === 'sponsoring' ? 'Sponsoring' : 'Premium'} ·{' '}
                  {eur(Number(l.montant_transaction))}
                </td>
                <td className="text-blanc">{l.payeur_nom ?? '—'}</td>
                <td className="text-blanc">{l.beneficiaire_nom}</td>
                <td>
                  <Badge>{l.type === 'sponsor_organisateur' ? 'Sponsor' : 'Parrainage'}</Badge>
                </td>
                <td className="text-fumee">
                  {(Number(l.taux_applique) * 100).toLocaleString('fr-BE')} % ·{' '}
                  {l.palier === 'annee1' ? '1ère année' : 'lifetime'}
                </td>
                <td>
                  <GradientAmount>{eur(Number(l.montant))}</GradientAmount>
                </td>
                <td>
                  <Badge filled={l.statut === 'cree'}>
                    {l.statut === 'cree' ? 'versée ✓' : l.statut === 'echoue' ? 'échouée ⚠' : l.statut}
                  </Badge>
                </td>
              </tr>
            ))}
          </Table>
          <p className="mt-4 text-right text-[14px] text-fumee">
            Total : <GradientAmount>{eur(total)}</GradientAmount>
          </p>
        </>
      )}
    </>
  );
}
